import { Builder } from '@builder.io/react'
import { LinkButtonRegisterProps } from 'components/ui-builder/common/button'
import CdnImage from 'components/util/CdnImage'
import { navigate } from 'gatsby'
import React from 'react'
import Bullet from '../../../images/BulletPoint.svg'
import { LinkButtonProp } from '../common/button'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

interface Props {
  backgroundColor: string
  cloudinaryHeroImage: any
  builderHeroImage: any
  imageAltText: string
  title: string
  headingText: string
  headingTextColor: string
  contentText: string
  headingType: HeadingType
  headingClass: HeadingClass
  textAlignment: Alignment
  buttonEnabled: boolean
  buttonProp: LinkButtonProp
  mobileBannerHeader: string
}

const DogStory = ({
  backgroundColor,
  builderHeroImage,
  imageAltText,
  title,
  headingText,
  headingTextColor,
  contentText,
  headingType,
  headingClass,
  textAlignment,
  buttonEnabled,
  buttonProp,
  mobileBannerHeader
}: Props) => {
  const renderImage = () => {
    return (
      <>
        {builderHeroImage && (
          <>
            <CdnImage
              className="index-2 relative mx-auto w-5/6 lg:w-full object-cover h-auto xl:h-[30rem] lg:max-h-[24rem] xl:max-h-[26rem] 2xl:max-h-80 rounded-xl lg:rounded-[30px] z-20 lg:block hidden"
              url={builderHeroImage}
              alt={imageAltText}
              lazyLoad={false}
              steps={[1080]}
              width={'500'}
              style={{
                top: '-15%',
                bottom: '-26%',
                transform: 'translateY(-10%)'
              }}
            />
            <CdnImage
              className="index-2 relative mx-auto w-5/6 lg:w-full object-cover h-auto xl:h-[30rem] lg:max-h-[24rem] xl:max-h-[26rem] 2xl:max-h-80 rounded-xl lg:rounded-[30px] z-20 lg:hidden block"
              url={builderHeroImage}
              alt={imageAltText}
              lazyLoad={false}
              steps={[1080]}
              width={'500'}
            />
          </>
        )}
      </>
    )
  }

  const renderCTAButton = (buttonEnabled: boolean, buttonText: string) => {
    if (buttonEnabled) {
      return (
        <button
          type={'submit'}
          className="mx-auto flex w-full items-center justify-center rounded-full bg-[#006685] px-5 py-3 text-base font-medium text-white md:w-56 md:text-lg"
          onClick={() => {
            if (typeof window.gtag !== 'undefined') {
              window.gtag('event', 'cta_recipe_of_the_month_add_recipe_cta_logged_in', {
                page: 'home_page_logged_in'
              })
            }
            navigate(`${buttonProp?.buttonUrl}`)
          }}
        >
          {buttonText}
        </button>
      )
    }

    return null
  }

  return (
    <div
      className="bg-image-none relative h-full w-full lg:w-[96%] xl:w-10/12 mx-auto md:bg-cover md:bg-bottom md:bg-no-repeat rounded-2xl xl:container"
      style={{ backgroundColor: backgroundColor }}
    >
      <img className="w-11/12 mx-auto justify-center pt-6 lg:hidden" src={mobileBannerHeader} alt={'banner'} />
      <div className="flex flex-col lg:flex-row items-center mx-auto justify-center 2xl:px-0 lg:-mb-16 pb-4 xl:pb-0 2xl:pb-4">
        {title && (
          <>
            <div className="bg-[#F8F2EC] mb-6 lg:mb-24 xl:mb-0 pr-4 xl:px-8 flex-col justify-center items-center hidden lg:flex">
              <img className="hidden lg:block" src={Bullet} alt={'DD logo'} />

              <p
                className="line-height-fix w-11/12 lg:w-full enable-list-styles text-base font-medium font-sans text-center my-4"
                dangerouslySetInnerHTML={{ __html: title }}
              />
            </div>
            <div className="bg-[#F8F2EC] w-11/12 flex-col justify-center items-center pb-6 -mt-6 flex lg:hidden">
              <img className="hidden lg:block" src={Bullet} alt={'DD logo'} />

              <p
                className="line-height-fix w-11/12 lg:w-full enable-list-styles text-base font-medium font-sans text-center my-4"
                dangerouslySetInnerHTML={{ __html: title }}
              />
            </div>
          </>
        )}

        <div className="bg-[#F8F2EC] w-11/12 block lg:hidden"> {renderImage()}</div>
        <div className="hidden lg:block"> {renderImage()}</div>
        <div className="bg-[#F8F2EC] w-11/12 lg:w-4/6 rounded-b-2xl lg:rounded-r-2xl lg:rounded-l-none p-6 lg:p-4 h-4/5 flex-col">
          <div className="w-full mx-auto lg:mr-auto lg:ml-6 lg:mt-4 xl:-mt-4 flex flex-col justify-center items-center lg:items-start lg:justify-start">
            {' '}
            <CustomHeading
              classList="line-height-fix w-full h-full tracking-tight text-gray-900 font-medium text-center lg:text-left lg:-mt-8"
              headingClass={headingClass}
              headingType={headingType}
              color={headingTextColor}
            >
              {headingText}
            </CustomHeading>
            <p
              className="line-height-fix lg:w-full xl:w-11/12 2xl:w-full enable-list-styles text-sm xl:text-base font-medium font-sans text-center lg:text-left"
              dangerouslySetInnerHTML={{ __html: contentText }}
            />
            <div className="mx-auto lg:mr-auto lg:ml-0 flex justify-start items-start">
              {renderCTAButton(buttonEnabled, `${buttonProp.loggedInButtonText}`)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DogStory
Builder.registerComponent(DogStory, {
  name: 'DogStory',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },

    {
      name: 'builderHeroImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },

    {
      name: 'imageAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: ''
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'contentText',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'textAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left'
    },
    {
      name: 'buttonEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      ...LinkButtonRegisterProps,
      name: 'buttonProp'
    },
    {
      name: 'mobileBannerHeader',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    }
  ]
})
